import { createIdMap } from 'components/version_mapping/utility'
import { atom } from 'infrastructure/state/jotai.wrapper'
import { INode } from 'shared/types/tree'
import {
  UpdateData,
  CreateNodeUpdateData,
  CreateLeafUpdateData,
} from '../nodeUpdates/types'
import { UpdateMethod, UpdateType } from '../nodeUpdates/utility'
import { NodeType } from 'shared/types/ingestion'
import {
  filterQuestionNoList,
  filterQuestionToApply,
  getMappedStandardsLists,
} from './helpers'

export const workingTreeAtom = atom<{ tree?: INode; resetTree?: () => void }>(
  {},
)
export const workingIdMapAtom = atom((get) => {
  const { tree } = get(workingTreeAtom)
  if (!tree) return
  return createIdMap(tree)
})

export const nodesToCascadeAtom = atom<INode[]>([])
export const treeIdAtom = atom<string>('')
export const versionIdAtom = atom<string>('')
export const standardsSelectedAtom = atom<string>('')

// Derived atom for creating skill lists operations
export const createSkillsListOperationsAtom = atom<UpdateData[]>((get) => {
  const nodesToCascade = get(nodesToCascadeAtom)
  const treeId = get(treeIdAtom)
  const versionId = get(versionIdAtom)

  return nodesToCascade.reduce<UpdateData[]>((acc, node) => {
    const questions = filterQuestionNoList(node)
    questions.forEach((question) => {
      const updateData: CreateNodeUpdateData = {
        type: UpdateType.node,
        ids: {
          nodeId: question.id,
          treeId: treeId,
          versionId: versionId,
        },
        method: UpdateMethod.create,
        params: {
          type: NodeType.SkillsList,
          title: 'Skills',
        },
      }
      acc.push(updateData)
    })
    return acc
  }, [])
})

// Derived atom for create skills list display metrics
export const createSkillsListMetricsAtom = atom((get) => {
  const nodesToCascade = get(nodesToCascadeAtom)

  const nodesToUpdate = nodesToCascade.filter(
    (node) => filterQuestionNoList(node).length > 0,
  ).length

  const totalQuestions = nodesToCascade.flatMap((node) =>
    filterQuestionNoList(node),
  ).length

  return {
    nodesToUpdate,
    totalQuestions,
  }
})

// Derived atom for mapped standards (used for both operations and display)
export const mappedStandardsAtom = atom((get) => {
  const nodesToCascade = get(nodesToCascadeAtom)
  const standardsSelected = get(standardsSelectedAtom)
  return getMappedStandardsLists(nodesToCascade, standardsSelected)
})

// Derived atom for cascading standards operations
export const cascadeStandardsOperationsAtom = atom<UpdateData[]>((get) => {
  const mappedStandards = get(mappedStandardsAtom)
  const treeId = get(treeIdAtom)
  const versionId = get(versionIdAtom)

  return Object.entries(mappedStandards).reduce<UpdateData[]>(
    (acc, [skillListId, leaves]) => {
      if (leaves.length > 0) {
        const updateData: CreateLeafUpdateData = {
          type: UpdateType.leaf,
          ids: {
            nodeId: skillListId,
            treeId: treeId,
            versionId: versionId,
          },
          method: UpdateMethod.create,
          params: {
            leaves: leaves,
          },
        }
        acc.push(updateData)
      }
      return acc
    },
    [],
  )
})

// Derived atom for cascade standards display metrics
export const cascadeStandardsMetricsAtom = atom((get) => {
  const mappedStandards = get(mappedStandardsAtom)
  const nodesToCascade = get(nodesToCascadeAtom)

  const totalLeavesToAdd = Object.values(mappedStandards).reduce(
    (sum, leaves) => sum + leaves.length,
    0,
  )

  const skillListCount = Object.keys(mappedStandards).length

  const nodesToUpdate = nodesToCascade.filter(
    (node) => filterQuestionToApply(node).length > 0,
  ).length

  const questionsNeedingSkillLists = nodesToCascade.flatMap((node) =>
    filterQuestionNoList(node),
  )
  const hasQuestionsNeedingSkillLists = questionsNeedingSkillLists.length > 0

  return {
    totalLeavesToAdd,
    skillListCount,
    nodesToUpdate,
    hasQuestionsNeedingSkillLists,
  }
})
